import { useState, useEffect, useMemo, useCallback } from "react";
import { apiService } from "../../../../services/api";
import { parseApiError } from "../../../../services/api/errorPresentation";
import { Ertragsprognose } from "../../../../types";

interface UseYieldDataProps {
  projectId: string;
}

/**
 * Hilfsfunktion: Durchschnitt über Werte berechnen, null-Werte ignorieren.
 * Gibt null zurück wenn keine gültigen Werte vorhanden.
 *
 * Duale Berechnung: Entspricht aggregate_yield_forecasts im Backend
 * (domain/yield_forecasts/service.py). Shared Fixtures in
 * backend_python/tests/fixtures/shared_calculation_fixtures.json
 * stellen die Konsistenz sicher.
 */
function avg(values: (number | undefined | null)[]): number | null {
  const valid = values.filter((v): v is number => v != null);
  if (valid.length === 0) return null;
  return valid.reduce((sum, v) => sum + v, 0) / valid.length;
}

export const useYieldData = ({ projectId }: UseYieldDataProps) => {
  const [allYieldForecasts, setAllYieldForecasts] = useState<Ertragsprognose[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Aggregierte aktive Ertragsprognose – client-seitig berechnet.
   * Ersetzt den bisherigen zweiten API-Call (getActiveProjectErtragsprognose).
   *
   * Berechnet Durchschnittswerte über alle aktiven Prognosen:
   * Bruttoertrag, Abzüge, P50/P75/P90, Unsicherheit.
   */
  const activeYieldForecast = useMemo<Ertragsprognose | null>(() => {
    const active = allYieldForecasts.filter((f) => f.aktiv);
    if (active.length === 0) return null;

    const count = active.length;

    // Bei nur einer aktiven Prognose: direkt zurückgeben (kein Averaging nötig)
    if (count === 1) return active[0];

    // Durchschnitte berechnen (analog zu Backend: aggregate_yield_forecasts)
    return {
      ...active[0], // Basis-Felder (id, project_id, etc.) von erster Prognose übernehmen
      bruttoertrag:
        active.reduce((sum, f) => sum + (f.bruttoertrag ?? 0), 0) / count,
      abzuege_gesamt:
        active.reduce((sum, f) => sum + (f.abzuege_gesamt ?? 0), 0) / count,
      nettoertrag_p50: avg(active.map((f) => f.nettoertrag_p50)) ?? undefined,
      nettoertrag_p75: avg(active.map((f) => f.nettoertrag_p75)) ?? undefined,
      nettoertrag_p90: avg(active.map((f) => f.nettoertrag_p90)) ?? undefined,
      unsicherheit: avg(active.map((f) => f.unsicherheit)) ?? undefined,
    };
  }, [allYieldForecasts]);

  // Initiales Laden (Mount)

  const loadYieldData = async (silent: boolean = false) => {
    if (!projectId) return;

    try {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);

      const yieldForecasts =
        await apiService.getProjectErtragsprognosen(projectId);
      setAllYieldForecasts(yieldForecasts);
    } catch (err) {
      setError(parseApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      loadYieldData();
    }
  }, [projectId]);

  // Granulare lokale Updates (kein erneuter API-Call nötig)

  /** Ersetzt eine Prognose im lokalen State mit der Backend-Response */
  const updateForecastLocally = useCallback((forecast: Ertragsprognose) => {
    setAllYieldForecasts((prev) =>
      prev.map((f) => (f.id === forecast.id ? forecast : f)),
    );
  }, []);

  /** Fügt eine neue Prognose zum lokalen State hinzu */
  const addForecastLocally = useCallback((forecast: Ertragsprognose) => {
    setAllYieldForecasts((prev) => [...prev, forecast]);
  }, []);

  /** Entfernt eine Prognose aus dem lokalen State */
  const removeForecastLocally = useCallback((forecastId: string) => {
    setAllYieldForecasts((prev) => prev.filter((f) => f.id !== forecastId));
  }, []);

  // Gezieltes Nachladen (nur eine Prognose, z.B. nach DeductionItem-Änderung)

  /** Lädt eine einzelne Prognose vom Server nach und aktualisiert den lokalen State */
  const reloadSingleForecast = useCallback(
    async (forecastId: string) => {
      try {
        const forecast = await apiService.getYieldForecast(
          projectId,
          forecastId,
        );
        setAllYieldForecasts((prev) =>
          prev.map((f) => (f.id === forecastId ? forecast : f)),
        );
      } catch {}
    },
    [projectId],
  );

  return {
    activeYieldForecast,
    allYieldForecasts,
    isLoading,
    error,
    loadYieldData,
    // Granulare Updates
    updateForecastLocally,
    addForecastLocally,
    removeForecastLocally,
    reloadSingleForecast,
  };
};
